import BugReportIcon from '@mui/icons-material/BugReport';
import ClearAll from '@mui/icons-material/ClearAll';
import DownloadIcon from '@mui/icons-material/Download';
import TerminalIcon from '@mui/icons-material/Terminal';
import saveAs from 'file-saver';
import { Box, Chip, IconButton, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { ChangeEvent, KeyboardEvent, SyntheticEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Diagnostic, DiagnosticSeverity } from 'vscode-languageserver-protocol';
import type { Range } from 'vscode-languageserver-types';
import { projectSlice } from '../../../redux/ProjectSlice';
import { RootState } from '../../../redux/Store';
import PullTab from '../PullTab';
import BottomDrawerTabPanel from './BottomDrawerTabPanel';
import FilterField from './FilterField';
import ProblemItem from './ProblemsItem';
import { LAYOUT_CONSTANTS } from '../../../utils/layout-constants';
import useResize from '../../../utils/hooks/useResize';

interface BottomDrawerProps {
  diagnostics: Diagnostic[];
  onSelectRange: (range: Range) => void;
  displayActivityIndicator: boolean;
  leftDrawerOpen?: boolean;
  leftDrawerWidth?: number;
  rightDrawerOpen?: boolean;
  rightDrawerWidth?: number;
}

type BottomDrawerTabDef = {
  index: number;
  Icon: typeof TerminalIcon;
  labelKey: string;
  labelDefault: string;
};

const BOTTOM_DRAWER_TABS: BottomDrawerTabDef[] = [
  { index: 0, Icon: TerminalIcon, labelKey: 'bottom.status.bar.console', labelDefault: 'Console' },
  { index: 1, Icon: BugReportIcon, labelKey: 'bottom.status.bar.bugs', labelDefault: 'Bugs' },
];

const BOTTOM_DRAWER_CONSOLE_TAB = BOTTOM_DRAWER_TABS[0].index;

const bottomDrawerTabSx = (drawerOpen: boolean) => ({
  height: '100%',
  minHeight: 40,
  px: 1.5,
  gap: 0.75,
  textTransform: 'none',
  color: 'text.primary',
  border: 'none',
  borderRadius: 0,
  backgroundColor: 'transparent',
  backgroundImage: 'none',
  boxShadow: 'none',
  position: 'relative',
  '&:hover': {
    backgroundColor: 'action.hover',
    backgroundImage: 'none',
  },
  '&.Mui-selected': {
    backgroundColor: drawerOpen ? 'accent.opaque' : 'transparent',
    backgroundImage: 'none',
    boxShadow: 'none',
    fontWeight: 600,
    color: 'text.primary',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 4,
      backgroundColor: 'accent.main',
    },
  },
  '&.Mui-selected:hover': {
    backgroundColor: drawerOpen ? 'accent.opaque' : 'transparent',
    backgroundImage: 'none',
  },
});

const panelHeightTransition = (theme: Theme, drawerOpen: boolean, disableTransition: boolean) =>
  disableTransition
    ? 'none'
    : theme.transitions.create('height', {
        easing: theme.transitions.easing.sharp,
        duration: drawerOpen ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
      });

const BottomDrawer = ({
  diagnostics,
  onSelectRange,
  leftDrawerOpen = false,
  leftDrawerWidth = LAYOUT_CONSTANTS.LEFT_DRAWER_TOTAL_WIDTH(LAYOUT_CONSTANTS.LEFT_DRAWER_DEFAULTS.CODE_EDITOR),
  rightDrawerOpen = false,
  rightDrawerWidth = 400,
}: BottomDrawerProps) => {
  const [filteredDiagnostics, setFilteredDiagnostics] = useState<Array<Diagnostic>>(diagnostics);
  const [displayedDiagnostics, setDisplayedDiagnostics] = useState<Array<Diagnostic>>(filteredDiagnostics);
  const [displayedConsoleLogs, setDisplayedConsoleLogs] = useState<Array<string>>([]);
  const [searched, setSearched] = useState<string>('');
  const [value, setValue] = useState(0);
  const dispatch = useDispatch();
  const store = useStore<RootState>();

  const bottomDrawer = useSelector((state: RootState) => state.project.bottomBarOpenDrawer);
  const { consoleDisplay, bottomDrawerHeight, loadedProject } = useSelector((state: RootState) => state.project);

  const { t } = useTranslation();
  const selectedTabAtPointerDownRef = useRef<number | null>(null);
  const keyboardTabActivationRef = useRef(false);
  const defaultHeight = LAYOUT_CONSTANTS.BOTTOM_DRAWER_EXPANDED_HEIGHT;
  const MIN_OPEN_HEIGHT = 64;
  // Seed internal height to 0 while closed to avoid first-drag jump after fresh load
  const [initialHeight, setInitialHeight] = useState<number>(bottomDrawer ? defaultHeight : 0);
  const suppressAutoCloseRef = useRef<boolean>(false);
  const pendingRestoreHeightRef = useRef<number | null>(null);
  const restoreHeightRafRef = useRef<number | null>(null);
  const consoleScrollRef = useRef<HTMLDivElement>(null);
  // Remember the last meaningful open height (non-zero unless user dragged closed)
  const lastOpenHeightRef = useRef<number>(bottomDrawerHeight ?? defaultHeight);
  const lastReportedHeightRef = useRef<number | null>(null);
  const [isHandleHover, setIsHandleHover] = useState(false);
  const maxHeight = useMemo(() => Math.max(160, Math.min(600, Math.floor(window.innerHeight * 0.6))), []);
  const {
    size: panelHeight,
    isResizing,
    enableResize,
    beginResizeFrom,
  } = useResize({
    initial: initialHeight,
    min: 0,
    max: maxHeight,
    axis: 'y',
    edge: 'top',
  });
  // Keep a global CSS var with the total bottom offset so content can pad for it
  useEffect(() => {
    const layoutPanelHeight = bottomDrawer ? panelHeight : 0;
    const totalOffsetPx = LAYOUT_CONSTANTS.BOTTOM_CONTROL_BAR_HEIGHT + LAYOUT_CONSTANTS.BOTTOM_DRAWER_STATUS_BAR_HEIGHT + layoutPanelHeight;
    document.documentElement.style.setProperty('--bottom-drawer-offset', `${totalOffsetPx}px`);
    return () => {
      // leave last known value
    };
  }, [bottomDrawer, panelHeight]);

  useEffect(() => {
    if (bottomDrawer) {
      return;
    }
    pendingRestoreHeightRef.current = null;
    if (restoreHeightRafRef.current != null) {
      cancelAnimationFrame(restoreHeightRafRef.current);
      restoreHeightRafRef.current = null;
    }
  }, [bottomDrawer]);

  useEffect(() => {
    if (!bottomDrawer || pendingRestoreHeightRef.current == null || restoreHeightRafRef.current != null) {
      return;
    }
    restoreHeightRafRef.current = requestAnimationFrame(() => {
      const restored = pendingRestoreHeightRef.current;
      restoreHeightRafRef.current = null;
      pendingRestoreHeightRef.current = null;
      if (restored == null || !store.getState().project.bottomBarOpenDrawer) {
        return;
      }
      setInitialHeight(restored);
    });
  }, [bottomDrawer, store]);

  useEffect(
    () => () => {
      if (restoreHeightRafRef.current != null) {
        cancelAnimationFrame(restoreHeightRafRef.current);
      }
    },
    []
  );

  useEffect(() => {
    setFilteredDiagnostics(diagnostics.filter((diag) => diag.severity !== DiagnosticSeverity.Hint));
  }, [diagnostics]);

  useEffect(() => {
    if (value === 1) {
      setDisplayedDiagnostics(filteredDiagnostics);
      requestDiagnosticSearch(searched);
    } else {
      setDisplayedDiagnostics(filteredDiagnostics);
      requestDiagnosticSearch('');
    }
    // Intentionally depend only on filteredDiagnostics; tab/search changes are handled elsewhere.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-sync displayed rows when diagnostics source changes
  }, [filteredDiagnostics]);

  useEffect(() => {
    if (value === 1) {
      requestDiagnosticSearch(searched);
    } else {
      requestDiagnosticSearch('');
    }
    // Intentionally omit searched; active-tab search text is applied in handleInput to avoid duplicate filtering.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-apply diagnostic filter only when switching tabs
  }, [value]);

  useEffect(() => {
    if (value === 0) {
      setDisplayedConsoleLogs(consoleDisplay);
      requestConsoleLogsSearch(searched);
    }
    // Intentionally depend only on consoleDisplay; search text changes are handled in handleInput.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-sync displayed console rows when source logs change
  }, [consoleDisplay]);

  useLayoutEffect(() => {
    if (!bottomDrawer || value !== 0) return;
    const el = consoleScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [bottomDrawer, displayedConsoleLogs, value]);

  const requestDiagnosticSearch = (searchedVal: string) => {
    const filteredRows = filteredDiagnostics.filter((diagnostic) => {
      const code = diagnostic.code as string;
      if (!diagnostic.code)
        return (
          diagnostic.message.toLowerCase().includes(searchedVal.toLowerCase()) ||
          `Line ${diagnostic.range.end.line + 1}: `.toLowerCase().includes(searchedVal.toLowerCase())
        );
      return (
        diagnostic.message.toLowerCase().includes(searchedVal.toLowerCase()) ||
        `Line ${diagnostic.range.end.line + 1}: `.toLowerCase().includes(searchedVal.toLowerCase()) ||
        `(${code})`.toLowerCase().includes(searchedVal.toLowerCase())
      );
    });
    setDisplayedDiagnostics(filteredRows);
  };

  const requestConsoleLogsSearch = (searchedVal: string) => {
    const filteredRows = consoleDisplay.filter((log) => {
      return log.toLowerCase().includes(searchedVal.toLowerCase());
    });
    setDisplayedConsoleLogs(filteredRows);
  };

  const handleInput = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearched(event.target.value);
    if (value === 0) {
      requestConsoleLogsSearch(event.target.value);
    } else if (value === 1) {
      requestDiagnosticSearch(event.target.value);
    }
  };

  const cancelSearch = () => {
    setSearched('');
    requestDiagnosticSearch('');
    requestConsoleLogsSearch('');
  };

  const openBottomDrawer = () => {
    if (store.getState().project.bottomBarOpenDrawer) return;
    const remembered = Math.min(maxHeight, Math.max(0, lastOpenHeightRef.current || 0));
    const restored = remembered > 0 ? remembered : MIN_OPEN_HEIGHT;
    pendingRestoreHeightRef.current = restored;
    suppressAutoCloseRef.current = true;
    dispatch(projectSlice.actions.toggleBottomBarOpenDrawer());
  };

  const closeBottomDrawer = () => {
    if (!store.getState().project.bottomBarOpenDrawer) return;
    const pendingRestore = pendingRestoreHeightRef.current;
    const heightSource = pendingRestore ?? panelHeight;
    const remembered = Math.min(maxHeight, Math.max(0, heightSource || 0));
    lastOpenHeightRef.current = remembered;
    dispatch(projectSlice.actions.setBottomDrawerHeight(remembered));
    dispatch(projectSlice.actions.toggleBottomBarOpenDrawer());
  };

  const handleDrawerPanelTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
    if (event.propertyName !== 'height' || event.target !== event.currentTarget) {
      return;
    }
    if (!bottomDrawer) {
      setInitialHeight(0);
    }
  };

  const animatedPanelHeight = bottomDrawer ? panelHeight : 0;

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
    if (!bottomDrawer) {
      openBottomDrawer();
    }
  };

  const handleSameTabToggle = () => {
    if (bottomDrawer) {
      closeBottomDrawer();
    } else {
      openBottomDrawer();
    }
  };

  const handleTabPointerDown = () => {
    selectedTabAtPointerDownRef.current = value;
  };

  const handleTabClick = (clickedIndex: number) => {
    if (keyboardTabActivationRef.current) {
      keyboardTabActivationRef.current = false;
      return;
    }
    const selectedBeforeClick = selectedTabAtPointerDownRef.current ?? value;
    selectedTabAtPointerDownRef.current = null;
    if (selectedBeforeClick !== clickedIndex) {
      return;
    }
    handleSameTabToggle();
  };

  const handleTabKeyDown = (event: KeyboardEvent, tabIndex: number) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    keyboardTabActivationRef.current = true;
    if (value === tabIndex) {
      handleSameTabToggle();
    }
  };

  const handleFocus = () => {
    if (!bottomDrawer) openBottomDrawer();
  };

  const handleClearConsole = () => {
    dispatch(projectSlice.actions.onClearConsoleDisplay());
  };

  const handleDownloadConsole = () => {
    const projectName = (loadedProject?.name ?? t('bottomDrawer.untitled-project', 'untitled')).replace(/[/\\:*?"<>|]/g, '_').trim() || 'untitled';
    const now = new Date();
    const date = now.toISOString().slice(0, 10);
    const time = now.toTimeString().slice(0, 8).replace(/:/g, '-');
    const fileName = `bps-console-${projectName}-${date}-${time}.txt`;
    const content = consoleDisplay.length ? consoleDisplay.join('\n') : '';
    const blob = new Blob([content], { type: 'text/plain' });
    saveAs(blob, fileName);
  };

  // Auto-close if user drags to near-zero height
  useEffect(() => {
    if (!isResizing && bottomDrawer && panelHeight <= 8) {
      if (suppressAutoCloseRef.current) {
        return;
      }
      lastOpenHeightRef.current = 0;
      dispatch(projectSlice.actions.setBottomDrawerHeight(0));
      dispatch(projectSlice.actions.toggleBottomBarOpenDrawer());
    }
  }, [isResizing, panelHeight, bottomDrawer, dispatch]);

  // Track last meaningful open height while the drawer is open
  useEffect(() => {
    if (bottomDrawer && panelHeight > 8 && !isResizing) {
      // Clamp to max just in case bounds changed
      const clamped = Math.min(panelHeight, maxHeight);
      if (clamped !== lastReportedHeightRef.current) {
        lastOpenHeightRef.current = clamped;
        lastReportedHeightRef.current = clamped;
        dispatch(projectSlice.actions.setBottomDrawerHeight(clamped));
      }
    }
  }, [bottomDrawer, panelHeight, maxHeight, isResizing, dispatch]);

  // Clear suppression once layout is stable (opened and has > 0 height) or drawer is closed
  useEffect(() => {
    if (suppressAutoCloseRef.current) {
      if (!bottomDrawer || isResizing || panelHeight > 8) {
        suppressAutoCloseRef.current = false;
      }
    }
  }, [bottomDrawer, isResizing, panelHeight]);

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: LAYOUT_CONSTANTS.BOTTOM_CONTROL_BAR_HEIGHT, // Sit directly above BottomControlBar
        left: `var(--left-drawer-offset, ${leftDrawerOpen ? leftDrawerWidth : 94}px)`,
        right: rightDrawerOpen ? rightDrawerWidth : 0,
        zIndex: (theme) => theme.zIndex.drawer - 2,
        transition: 'var(--bottom-bar-transition, left 0.225s, right 0.225s)',
        willChange: 'left, right',
      }}
    >
      {/* Highlight line during resize */}
      {(isResizing || isHandleHover) && (
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            bgcolor: 'text.primary',
            pointerEvents: 'none',
            zIndex: (theme) => theme.zIndex.drawer - 1,
          }}
        />
      )}
      {/* Pull tab - top middle */}
      <Box
        sx={{
          position: 'absolute',
          top: -23,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      >
        <PullTab
          orientation="horizontal"
          onMouseDown={(e) => {
            const startX = (e as unknown as MouseEvent).clientX;
            const startY = (e as unknown as MouseEvent).clientY;
            const wasOpen = bottomDrawer;
            let moved = false;
            const threshold = 3; // pixels

            const onMove = (ev: MouseEvent) => {
              if (!moved) {
                const dx = ev.clientX - startX;
                const dy = ev.clientY - startY;
                if (Math.abs(dx) >= threshold || Math.abs(dy) >= threshold) {
                  moved = true;
                  if (!wasOpen) {
                    // Start drag from closed: compute current desired height from total drag distance
                    const desired = Math.max(0, Math.min(maxHeight, startY - ev.clientY));
                    // Pre-seed initial height and mark resizing BEFORE opening so the first open frame
                    // renders with the correct height and no transition/jump.
                    setInitialHeight(desired);
                    suppressAutoCloseRef.current = true;
                    beginResizeFrom(ev as MouseEvent, desired);
                    // Now open the drawer
                    dispatch(projectSlice.actions.toggleBottomBarOpenDrawer());
                  } else {
                    enableResize(ev as MouseEvent);
                  }
                  // After handing off to resize hook, stop our provisional listeners
                  document.removeEventListener('mousemove', onMove);
                  document.removeEventListener('mouseup', onUp);
                }
              }
            };

            const onUp = () => {
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
              if (!moved) {
                // Treat as click toggle
                if (!wasOpen) {
                  openBottomDrawer();
                } else {
                  closeBottomDrawer();
                }
              }
            };

            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          }}
          onMouseEnter={() => setIsHandleHover(true)}
          onMouseLeave={() => setIsHandleHover(false)}
          cursor="row-resize"
          position="top"
        />
      </Box>

      {/* The status bar is always visible */}
      <Box
        sx={(theme) => ({
          bgcolor: theme.vars?.palette?.background?.consoleHeader,
          color: theme.vars?.palette?.text?.primary,
          borderTop: 1,
          borderColor: theme.vars?.palette?.divider,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pl: 1,
          pr: 1,
          height: 40,
          overflow: 'visible',
        })}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ height: '100%' }}>
          <Tabs
            value={value}
            onChange={handleTabChange}
            selectionFollowsFocus
            aria-label={t('bottomDrawer.tabs.ariaLabel', 'Console and problem tabs')}
            variant="standard"
            sx={{
              minHeight: 40,
              height: '100%',
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTabs-flexContainer': {
                height: '100%',
              },
            }}
          >
            {BOTTOM_DRAWER_TABS.map(({ index, Icon, labelKey, labelDefault }) => (
              <Tab
                key={index}
                id={`bottom-bar-tab-${index}`}
                aria-controls={`bottom-bar-tabpanel-${index}`}
                icon={<Icon fontSize="small" />}
                iconPosition="start"
                label={t(labelKey, labelDefault)}
                onMouseDown={handleTabPointerDown}
                onClick={() => handleTabClick(index)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
                sx={() => bottomDrawerTabSx(bottomDrawer)}
              />
            ))}
          </Tabs>
          {/* Bug Count*/}
          <Chip
            label={filteredDiagnostics.length}
            size="small"
            sx={(theme) => ({
              height: 22,
              fontSize: '0.75rem',
              fontWeight: 600,
              backgroundColor: theme.vars?.palette?.accent?.opaque,
              color: theme.vars?.palette?.text?.primary,
            })}
          />
        </Stack>

        <Stack direction={'row'} alignItems="center" spacing={1}>
          {/* Clear Console Button - only show when Console tab is active */}
          {value === BOTTOM_DRAWER_CONSOLE_TAB && (
            <>
              <Tooltip title={t('bottomDrawer.tooltip.clear-console', 'Clear Console')}>
                <IconButton
                  aria-label={t('aria-label.clear-console', 'clear console')}
                  onClick={handleClearConsole}
                  size="small"
                  sx={(theme) => ({ color: theme.vars?.palette?.accent?.main })}
                >
                  <ClearAll />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('bottomDrawer.tooltip.download-console', 'Download Console')}>
                <IconButton
                  aria-label={t('aria-label.download-console', 'download console')}
                  onClick={handleDownloadConsole}
                  size="small"
                  sx={(theme) => ({ color: theme.vars?.palette?.accent?.main })}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
          <FilterField searched={searched} handleInput={handleInput} cancelSearch={cancelSearch} onFocus={handleFocus} />
        </Stack>
      </Box>

      {/* Expandable/collapsible panel — tabpanels stay mounted so tab aria-controls stay valid */}
      <Box
        onTransitionEnd={handleDrawerPanelTransitionEnd}
        sx={(theme) => ({
          height: animatedPanelHeight,
          overflow: 'hidden',
          width: '100%',
          bgcolor: 'background.console',
          transition: panelHeightTransition(theme, bottomDrawer, isResizing),
        })}
      >
        <BottomDrawerTabPanel index={0} value={value} drawerOpen={bottomDrawer}>
          <Box
            sx={{
              height: panelHeight,
              width: '100%',
              overflow: 'hidden',
              bgcolor: 'background.console',
            }}
          >
            <Stack
              ref={consoleScrollRef}
              data-testid="console-scroll-container"
              overflow="auto"
              direction={'column'}
              sx={{
                height: '100%',
                width: '100%',
                bgcolor: 'background.console',
                py: 2,
                px: 2,
              }}
            >
              {displayedConsoleLogs.length > 0 && (
                <Typography
                  variant="body2"
                  component="div"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '100%', userSelect: 'text' }}
                >
                  {displayedConsoleLogs.join('\n')}
                </Typography>
              )}
            </Stack>
          </Box>
        </BottomDrawerTabPanel>
        <BottomDrawerTabPanel index={1} value={value} drawerOpen={bottomDrawer}>
          <Box
            sx={{
              height: panelHeight,
              width: '100%',
              overflow: 'hidden',
              bgcolor: 'background.console',
            }}
          >
            <Stack
              overflow="auto"
              sx={{
                height: '100%',
                width: '100%',
                bgcolor: 'background.console',
                py: 2,
                px: 2,
              }}
            >
              {displayedDiagnostics.length > 0 ? (
                displayedDiagnostics.map((diag, index) => {
                  return <ProblemItem key={index} index={index} diagnostic={diag} onSelectRange={onSelectRange} />;
                })
              ) : (
                <Box sx={styles.diagnosticContainer}>
                  <Box sx={styles.diagnosticTextContainer}>
                    <Typography variant="body2">
                      <Trans i18nKey="bottom.status.bar.no.problems.text">No problems have been detected.</Trans>
                    </Typography>
                  </Box>
                </Box>
              )}
            </Stack>
          </Box>
        </BottomDrawerTabPanel>
      </Box>
    </Box>
  );
};

export default BottomDrawer;

const styles = {
  problemText: {
    padding: '2',
    color: 'white',
  },

  diagnosticContainer: {
    width: '100%',
    alignItems: 'left',
    justifyContent: 'left',
  },
  problemContainerHover: {
    width: '100%',
    alignItems: 'left',
    justifyContent: 'left',
    backgroundColor: 'spheroGrey.main',
  },
  diagnosticTextContainer: {},
  diagnosticText: {
    fontSize: 12,
  },
  diagnosticSourceText: {
    color: 'spheroGrey.main',
  },
};
